import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { useAuth } from '@/hooks/useAuth'
import { setApiCustomerContext } from '@/lib/api-customer-context'

type CustomerIdContextType = {
  customerId: string | null
  setCustomerId: (id: string | null, withNavigateHome?: boolean) => void
}

const CustomerIdContext = createContext<CustomerIdContextType | undefined>(
  undefined,
)

export function CustomerIdProvider({ children }: { children: ReactNode }) {
  const [customerId, setCustomerIdState] = useState<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    isLoading,
    isAuthenticated,
    isCustomerUser,
    isLangguthUser,
    customerId: customerIdFromAuth,
  } = useAuth()

  useEffect(() => {
    setApiCustomerContext({
      isLangguth: Boolean(isLangguthUser),
      selectedCustomerId: customerId,
    })
  }, [isLangguthUser, customerId])

  const setCustomerId = useCallback(
    (newValue: string | null, withNavigateHome = false) => {
      if (customerId === newValue) return
      const done = () => {
        setCustomerIdState(newValue)
        if (isLangguthUser) {
          void queryClient.invalidateQueries()
        }
      }
      if (withNavigateHome) {
        Promise.resolve(navigate({ to: '/' })).finally(done)
      } else {
        done()
      }
    },
    [customerId, isLangguthUser, navigate, queryClient],
  )

  useEffect(() => {
    if (
      isCustomerUser &&
      !isLoading &&
      isAuthenticated &&
      customerIdFromAuth &&
      customerId !== customerIdFromAuth
    ) {
      setCustomerIdState(customerIdFromAuth)
    }
  }, [
    isCustomerUser,
    isLoading,
    isAuthenticated,
    customerIdFromAuth,
    customerId,
  ])

  return (
    <CustomerIdContext.Provider value={{ customerId, setCustomerId }}>
      {children}
    </CustomerIdContext.Provider>
  )
}

export function useCustomerId() {
  const ctx = useContext(CustomerIdContext)
  if (!ctx) {
    throw new Error('useCustomerId must be used within a CustomerIdProvider')
  }
  return ctx
}
